import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';

import { FormulaAssumption } from '@futrli-sdk/predict';
import { useCrudActivityContext } from '@futrli/shared-services';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import {
  getOutputOccursOnData,
  checkFormulaValidation,
  getAvailableLineData,
  getFormulaDataContent
} from './utils';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { FormulaError, PaymentTreatment, PredictionType, RepeatingPattern } from '../../../types';
import { FormulaMethod } from '../../../components/formula/formula-method';
import { FormulaReferenceOption } from '../../../../../utils/formulas/types';
import { PredictionFormulaError } from '../../../components/formula/components/prediction-formula-error';
import { LAST_DAY_OF_MONTH, LAST_DAY_OF_MONTH_NUM, TRACKING_EVENTS } from '../../../../../core/constants';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { PROFIT_LOSS_ID, BALANCE_SHEET_ID, NON_FINANCIAL_DRIVERS_ID } from '../../../components/formula/const';
import { trackEvent } from '../../../../../core/tracker';
import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictionsInfoContext } from '../../../../detail/context/predictions-info-context';
import { deLocalizeFormula, localizeFormula } from '../../../../../utils/formulas/i18n-utils';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const FormulaPrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const intl = useIntl();
  const { nodeStaticIds } = useLocalisedConstantsContext();

  const {
    accountIndex,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    lineData,
    paymentDataSaveParams,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    setHideAccountsImpacted,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useStandardOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();
  const { forecast } = usePredictionsInfoContext();

  const [formulaContent, setFormulaContent] = useState('');
  const [validFormulaContent, setValidFormulaContent] = useState('');

  const [formulaContentTouched, setFormulaContentTouched] = useState(false);
  const [formulaError, setFormulaError] = useState({ error_code: FormulaError.NO_ERROR, is_valid: true });
  const [formulaPreviewImpactError, setFormulaPreviewImpactError] = useState({
    error_code: FormulaError.NO_ERROR,
    is_valid: true
  });
  const [formulaFrequencyOnDay, setFormulaFrequencyOnDay] = useState(LAST_DAY_OF_MONTH);
  const [formulaFrequencyOnMonth, setFormulaFrequencyOnMonth] = useState('1');
  const [formulaEditorVersion, setFormulaEditorVersion] = useState(0);
  const [initialFormulaContent, setInitialFormulaContent] = useState('');
  const [outputFrequency, setOutputFrequency] = useState<RepeatingPattern>(RepeatingPattern.monthly);

  const isActive = predictionType === PredictionType.FORMULA;
  const isInvalidFormula = !formulaError.is_valid || formulaContent.length === 0 || formulaContent === '\n';
  const isInvalidFormulaFromPreviewImpacts = !previewImpactsResponse?.is_valid;

  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();
  const onlyShowProfitAndLoss = forecast?.kind === 'budget';

  const referenceOptions = useMemo((): FormulaReferenceOption[] | undefined => {
    if (lineData) {
      const parsedInitialOptions = getAvailableLineData(
        intl,
        nodeStaticIds,
        lineData.profit_and_loss,
        intl.formatMessage({ id: 'financial_fields.type.profit_and_loss', defaultMessage: 'Profit & Loss' }),
        lineData.balance_sheet,
        intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
        lineData.non_financial_drivers_and_groups,
        intl.formatMessage({ id: 'reporting.grouping.drivers', defaultMessage: 'Drivers' }),
        onlyShowProfitAndLoss
      );

      // Formulas shouldn't be able to directly @reference themselves, so filter that out
      if (isEditing && urlPredictionId) {
        const filteredOptions = parsedInitialOptions.filter(o => o.id.indexOf(urlPredictionId) < 0);
        return filteredOptions;
      } else {
        return parsedInitialOptions;
      }
    }
  }, [isEditing, lineData, onlyShowProfitAndLoss, urlPredictionId, intl, nodeStaticIds]);

  const formulaErrorForDisplay = useMemo(() => {
    if (!formulaPreviewImpactError.is_valid) {
      return formulaPreviewImpactError;
    }
    return formulaError;
  }, [formulaError, formulaPreviewImpactError]);

  // *********
  // * Utils *
  // *********

  const refreshAccountsImpactPreview = useCallback(() => {
    const validFormulaContentEmpty = !validFormulaContent || validFormulaContent === '\n';

    // if cashflow disabled / current formula is not valid, do not refresh data
    if (isCashFlowDisabled || isInvalidFormula || validFormulaContentEmpty) {
      return;
    }

    if (accountIndex && predictionName) {
      const newParams = getFormulaDataContent({
        predictionName,
        vatPercentage,
        accountIndex,
        formulaContent: validFormulaContent,
        outputFrequency,
        formulaFrequencyOnDay,
        formulaFrequencyOnMonth,
        paymentTreatment
      });

      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [
    accountIndex,
    formulaFrequencyOnDay,
    formulaFrequencyOnMonth,
    isCashFlowDisabled,
    isInvalidFormula,
    outputFrequency,
    paymentTreatment,
    predictionName,
    setImpactPreviewParams,
    validFormulaContent,
    vatPercentage
  ]);

  const restoreInitialContent = useCallback(() => {
    setOutputFrequency(RepeatingPattern.monthly);
    setFormulaContent('');
    setValidFormulaContent('');
    setFormulaContentTouched(false);
    setFormulaFrequencyOnDay(LAST_DAY_OF_MONTH);
    setFormulaFrequencyOnMonth('1');
    setFormulaError({ error_code: FormulaError.NO_ERROR, is_valid: true });
    setInitialFormulaContent('');
    setFormulaEditorVersion(version => version + 1);
    return true;
  }, []);

  const restoreLoadedContent = useCallback(() => {
    const parsedInitialOptions = referenceOptions;
    const formulaFetchedPrediction = predictionData as FormulaAssumption | undefined;

    if (formulaFetchedPrediction && parsedInitialOptions) {
      setOutputFrequency(formulaFetchedPrediction.output_frequency as RepeatingPattern);
      if (typeof formulaFetchedPrediction.output_occurs_on_day === 'number') {
        setFormulaFrequencyOnDay(
          formulaFetchedPrediction.output_occurs_on_day === LAST_DAY_OF_MONTH_NUM
            ? LAST_DAY_OF_MONTH
            : String(formulaFetchedPrediction.output_occurs_on_day)
        );
      }
      if (typeof formulaFetchedPrediction.output_occurs_on_month === 'number') {
        setFormulaFrequencyOnMonth(String(formulaFetchedPrediction.output_occurs_on_month));
      }
      const localizedFormula = localizeFormula(formulaFetchedPrediction.formula);
      setFormulaContent(localizedFormula);
      setInitialFormulaContent(localizedFormula);
      setFormulaEditorVersion(version => version + 1);
      return true;
    }
    return false;
  }, [predictionData, referenceOptions]);

  const save = useCallback(async () => {
    const params = {
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      formula: deLocalizeFormula(formulaContent),
      output_frequency: outputFrequency,
      ...getOutputOccursOnData(outputFrequency, formulaFrequencyOnDay, formulaFrequencyOnMonth),
      payment_type_data: paymentDataSaveParams
    };

    let created = false;
    if (urlPredictionId && !isQuickCopy) {
      created = await handleSaveExisting(params);
    } else {
      created = !!(await handleSaveNew(params));
      if (created) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.CREATE_PREDICTION_FORMULA_NEW);
      }
    }

    return created;
  }, [
    accountIndex,
    formulaContent,
    formulaFrequencyOnDay,
    formulaFrequencyOnMonth,
    handleSaveExisting,
    handleSaveNew,
    isQuickCopy,
    outputFrequency,
    paymentDataSaveParams,
    paymentTreatment,
    predictionName,
    urlPredictionId,
    vatPercentage
  ]);

  const validateFormula = useCallback(() => {
    if (formulaContentTouched) {
      const contentWithReplacedValues = formulaContent
        .replace(PROFIT_LOSS_ID, '')
        .replace(BALANCE_SHEET_ID, '')
        .replace(NON_FINANCIAL_DRIVERS_ID, '');

      const isValidFormula = checkFormulaValidation(contentWithReplacedValues, outputFrequency);
      setFormulaError({
        error_code: isValidFormula ? FormulaError.NO_ERROR : FormulaError.ERROR_INVALID_SYNTAX,
        is_valid: isValidFormula
      });

      if (previewImpactsResponse) {
        setFormulaPreviewImpactError({
          error_code: previewImpactsResponse.error_code,
          is_valid: previewImpactsResponse.is_valid
        });
      }

      if (isValidFormula) {
        setValidFormulaContent(formulaContent);
      }

      return isValidFormula;
    }
  }, [formulaContent, formulaContentTouched, outputFrequency, previewImpactsResponse]);

  // ************
  // * Handlers *
  // ************

  const handleFormulaContentChange = useCallback(
    (content: string) => {
      if (content !== formulaContent) {
        setFormulaContent(content);
        if (content && content !== '\n') {
          setPredictionContentTouched(true);
          setFormulaContentTouched(true);
        }
      }
    },
    [formulaContent, setPredictionContentTouched]
  );

  const handleOutputFrequencyChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setOutputFrequency(event.target.value as RepeatingPattern);
    setFormulaFrequencyOnDay(LAST_DAY_OF_MONTH);
  };

  const handleFormulaFrequencyOnDayChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setFormulaFrequencyOnDay(event.target.value);
  };

  const handleFormulaFrequencyOnMonthChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setFormulaFrequencyOnMonth(event.target.value);
  };

  const handleSave = useCallback(async () => {
    addCreating('formula-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('formula-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('formula-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('formula-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, resetUrlData, restoreInitialContent, save]);

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      const contentWasRestored =
        (isEditing || isQuickCopy) && isActive ? restoreLoadedContent() : restoreInitialContent();
      if (contentWasRestored) {
        isFirstRun.current = false;
      }
    }
  }, [isActive, isEditing, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Validate data if content changes
  useEffect(() => {
    if (isActive) {
      validateFormula();
    }
  }, [isActive, validateFormula, previewImpactsResponse]);

  // Reload impact data if valid data changes
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(isInvalidFormula || isInvalidFormulaFromPreviewImpacts);
      setHideAccountsImpacted(isInvalidFormula);
    }
  }, [isActive, isInvalidFormula, isInvalidFormulaFromPreviewImpacts, setIsSaveDisabled, setHideAccountsImpacted]);

  return (
    <PredictionEditor
      footerContent={
        <PredictionFormulaError
          formulaContent={formulaContent}
          formulaError={formulaErrorForDisplay}
          show={formulaContentTouched}
        />
      }
      formError={!formulaError.is_valid && formulaContentTouched}
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      formulaEditorOptions={referenceOptions}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      {referenceOptions && (
        <FormulaMethod
          key={formulaEditorVersion}
          PredictionHeader={PredictionHeader}
          handleFormulaContentChange={handleFormulaContentChange}
          outputFrequency={outputFrequency}
          handleOutputFrequencyChange={handleOutputFrequencyChange}
          formulaFrequencyOnDay={formulaFrequencyOnDay}
          handleFormulaFrequencyOnDayChange={handleFormulaFrequencyOnDayChange}
          formulaFrequencyOnMonth={formulaFrequencyOnMonth}
          handleFormulaFrequencyOnMonthChange={handleFormulaFrequencyOnMonthChange}
          initialEditorOptions={referenceOptions}
          initialFormulaContent={initialFormulaContent}
        />
      )}
    </PredictionEditor>
  );
};
